/**
 * StripeEventsPanel — Realtime overzicht van alle Stripe webhook-events.
 *
 * Toont per event: type, factuur/klant, bedrag, status (processed / failed /
 * queued for retry), en biedt een manual "Retry" knop voor gefaalde events.
 *
 * Gebruikt:
 *   GET  /api/admin/stripe/events?page&limit&event_type&result&search
 *   POST /api/admin/stripe/events/{event_id}/retry
 */
import React, { useCallback, useEffect, useState } from "react";
import { adminApi, formatApiError } from "../../../context/AuthContext";
import { toast } from "sonner";
import { Button } from "../../ui/button";
import {
  RefreshCw, Loader2, Search, RotateCw, CheckCircle2, XCircle,
  Clock, AlertTriangle, Zap, Filter,
} from "lucide-react";
import {
  AdminSectionCard, AdminSearchInput, AdminFilterTabs,
  AdminStatusBadge, AdminEmptyState, AdminSkeleton,
} from "../ui";

const EVENT_TYPES = [
  { value: "", label: "Alle types" },
  { value: "checkout.session.completed", label: "Checkout voltooid" },
  { value: "checkout.session.expired", label: "Checkout verlopen" },
  { value: "checkout.session.async_payment_failed", label: "Async mislukt" },
  { value: "payment_intent.succeeded", label: "Betaling geslaagd" },
  { value: "payment_intent.payment_failed", label: "Betaling mislukt" },
  { value: "payment_intent.canceled", label: "Betaling geannuleerd" },
  { value: "charge.refunded", label: "Terugbetaling" },
  { value: "charge.dispute.created", label: "Dispute" },
];

const RESULT_FILTERS = [
  { key: "", label: "Alle" },
  { key: "processed", label: "Verwerkt" },
  { key: "queued", label: "Wachtrij" },
  { key: "failed", label: "Mislukt" },
];

function fmtMoney(v, cur = "EUR") {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("nl-NL", {
      style: "currency", currency: (cur || "EUR").toUpperCase(),
      minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(Number(v));
  } catch { return `${cur} ${Number(v).toFixed(2)}`; }
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function eventBadge(item) {
  if (item.processed) return { tone: "success", label: "Verwerkt", icon: CheckCircle2 };
  if (item.processing_failed_at) return { tone: "danger", label: `Mislukt (${item.retry_count})`, icon: XCircle };
  if (item.next_retry_at) return { tone: "warning", label: "Wachtrij", icon: Clock };
  return { tone: "muted", label: "Onbekend", icon: AlertTriangle };
}

export default function StripeEventsPanel() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState({});
  const [filter, setFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [search, setSearch] = useState("");
  const limit = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(limit),
      });
      if (filter) params.set("result", filter);
      if (typeFilter) params.set("event_type", typeFilter);
      if (search) params.set("search", search);
      const r = await adminApi.get(`/admin/stripe/events?${params.toString()}`);
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e, "Events laden mislukt"));
    } finally {
      setLoading(false);
    }
  }, [page, filter, typeFilter, search]);

  useEffect(() => { load(); }, [load]);

  const retry = async (eventId) => {
    setRetrying((s) => ({ ...s, [eventId]: true }));
    try {
      const r = await adminApi.post(`/admin/stripe/events/${eventId}/retry`);
      toast.success(`Event ${eventId.slice(0, 15)}… ${r.data.status}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e, "Retry mislukt"));
    } finally {
      setRetrying((s) => ({ ...s, [eventId]: false }));
    }
  };

  return (
    <div className="space-y-5" data-testid="stripe-events-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-chivo font-bold text-lg text-brand-900 flex items-center gap-2">
            <Zap className="w-5 h-5 text-brand-500" /> Stripe Events
          </h3>
          <p className="text-xs text-ink-500">
            Realtime overzicht van alle webhook-events. Retry mislukte events handmatig.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={load}
          disabled={loading}
          className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500"
          data-testid="events-refresh"
        >
          {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
          Vernieuwen
        </Button>
      </div>

      {/* Filters */}
      <AdminSectionCard padded={false}>
        <div className="p-4 flex items-center gap-3 flex-wrap">
          <AdminFilterTabs
            items={RESULT_FILTERS}
            value={filter}
            onChange={(k) => { setFilter(k); setPage(1); }}
            testidPrefix="events-filter"
          />
          <select
            value={typeFilter}
            onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
            className="text-xs h-10 px-3 rounded-full border border-slate-200 hover:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 bg-white"
            data-testid="events-type-filter"
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-3">
            <AdminSearchInput
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
              placeholder="Zoek factuur, tracking, session…"
              testid="events-search"
            />
            <div className="text-xs text-ink-500 whitespace-nowrap font-medium">
              {total} events
            </div>
          </div>
        </div>
      </AdminSectionCard>

      {/* Table */}
      {loading ? (
        <AdminSkeleton variant="table" rows={6} cols={6} />
      ) : items.length === 0 ? (
        <AdminEmptyState
          icon={Filter}
          title="Geen events gevonden"
          description="Pas filters aan of wacht op nieuwe Stripe webhooks."
          testid="events-empty"
        />
      ) : (
        <AdminSectionCard padded={false} testid="events-list-card">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-chivo font-bold">
                <tr>
                  <th className="text-left px-4 py-3">Datum</th>
                  <th className="text-left px-4 py-3">Type</th>
                  <th className="text-left px-4 py-3">Factuur</th>
                  <th className="text-left px-4 py-3">Klant</th>
                  <th className="text-right px-4 py-3">Bedrag</th>
                  <th className="text-left px-4 py-3">Referentie</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Actie</th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => {
                  const badge = eventBadge(it);
                  const canRetry = !it.processed && it.retry_count >= 0;
                  return (
                    <tr key={it.event_id} className="border-b border-slate-100 last:border-b-0 hover:bg-brand-50/40" data-testid={`event-row-${it.event_id}`}>
                      <td className="px-4 py-3 text-ink-500 font-mono-num tabular-nums whitespace-nowrap">
                        {fmtDate(it.received_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-xs font-mono text-brand-900">{it.type}</span>
                      </td>
                      <td className="px-4 py-3 font-mono-num tabular-nums text-brand-900">
                        {it.invoice_number || "—"}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-ink-900 truncate max-w-[160px]">{it.customer_name || "—"}</div>
                        <div className="text-[11px] text-ink-500 truncate max-w-[160px]">{it.customer_email || ""}</div>
                      </td>
                      <td className="px-4 py-3 text-right font-mono-num tabular-nums font-semibold text-brand-900 whitespace-nowrap">
                        {fmtMoney(it.amount, it.currency)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-[11px] text-ink-500 font-mono truncate max-w-[180px]" title={it.session_id || it.payment_intent}>
                          {(it.session_id || it.payment_intent || "").slice(0, 22) || "—"}
                        </div>
                        {it.tracking_number && (
                          <div className="text-[11px] text-brand-500 font-mono">{it.tracking_number}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <AdminStatusBadge tone={badge.tone} label={badge.label} icon={badge.icon} size="xs" />
                        {it.last_error && (
                          <div className="text-[10px] text-red-600 mt-1 max-w-[220px] truncate" title={it.last_error}>
                            {it.last_error}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {canRetry ? (
                          <Button
                            size="sm"
                            onClick={() => retry(it.event_id)}
                            disabled={!!retrying[it.event_id]}
                            className="h-7 text-xs bg-brand-900 hover:bg-brand-700 text-white rounded-full px-3"
                            data-testid={`event-retry-${it.event_id}`}
                          >
                            {retrying[it.event_id] ? (
                              <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                            ) : (
                              <RotateCw className="w-3 h-3 mr-1" />
                            )}
                            Retry
                          </Button>
                        ) : (
                          <span className="text-[10px] text-ink-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </AdminSectionCard>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-between text-sm" data-testid="events-pagination">
          <div className="text-ink-500">
            Pagina {page} / {Math.ceil(total / limit)}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="rounded-full border-slate-200 hover:border-brand-500"
              data-testid="events-prev"
            >
              Vorige
            </Button>
            <Button
              variant="outline" size="sm"
              onClick={() => setPage((p) => p + 1)}
              disabled={page * limit >= total}
              className="rounded-full border-slate-200 hover:border-brand-500"
              data-testid="events-next"
            >
              Volgende
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
